import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Chip, CircularProgress, Alert, Button, Grid, Divider, Avatar, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, TablePagination, Tooltip, IconButton, ButtonGroup
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsIcon from '@mui/icons-material/Sports';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

const getJwt = () => localStorage.getItem('feathers-jwt');

function LegaDetailPage() {
    const { legaId } = useParams();
    const navigate = useNavigate();
    const [lega, setLega] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Gestione query string anche in hash router
    function getTabFromUrl() {
        let tabParam = null;
        // 1. Prova da search (BrowserRouter)
        const searchParams = new URLSearchParams(window.location.search);
        tabParam = searchParams.get('tab');
        // 2. Se non trovato, prova da hash (HashRouter)
        if (!tabParam && window.location.hash) {
            const hash = window.location.hash;
            const idx = hash.indexOf('?');
            if (idx !== -1) {
                const hashQuery = hash.substring(idx + 1);
                const hashParams = new URLSearchParams(hashQuery);
                tabParam = hashParams.get('tab');
            }
        }
        if (tabParam === 'giornate') return 1;
        if (tabParam === 'classifiche') return 2;
        return 0;
    }
    const [tab, setTab] = useState(getTabFromUrl());
    const [giornate, setGiornate] = useState([]);
    const [loadingGiornate, setLoadingGiornate] = useState(false);
    const [errorGiornate, setErrorGiornate] = useState(null);

    // State per Classifiche
    const [classifiche, setClassifiche] = useState([]);
    const [loadingClassifiche, setLoadingClassifiche] = useState(false);
    const [errorClassifiche, setErrorClassifiche] = useState(null);
    const [filterStagioneClassifica, setFilterStagioneClassifica] = useState('all');
    const [teamsData, setTeamsData] = useState(new Map()); // Map teamId -> team data (foto, etc)

    // Filtri e paginazione Giornate
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStagione, setFilterStagione] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
    const [dateTo, setDateTo] = useState('');
    const [pageG, setPageG] = useState(0);
    const [rowsPerPageG, setRowsPerPageG] = useState(10);

    const stagioneOptions = React.useMemo(() => {
        const ids = Array.from(new Set((giornate || []).map(g => g.stagioneId).filter(Boolean)));
        return ids;
    }, [giornate]);

    const filteredGiornate = React.useMemo(() => {
        const term = (searchTerm || '').toLowerCase();
        return (giornate || []).filter(g => {
            const matchesSearch = !term ||
                String(g.nome || '').toLowerCase().includes(term) ||
                String(g.numero ?? '').toLowerCase().includes(term) ||
                String(g.id).includes(term);
            const matchesStagione = filterStagione === 'all' || g.stagioneId === Number(filterStagione);
            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'active' && g.flAttivo === 1 && g.flAnnullato !== 1) ||
                (filterStatus === 'inactive' && g.flAttivo === 0 && g.flAnnullato !== 1) ||
                (filterStatus === 'cancelled' && g.flAnnullato === 1);
            const fromOk = !dateFrom || (g.dataInizio && new Date(g.dataInizio) >= new Date(dateFrom));
            const toOk = !dateTo || (g.dataFine ? new Date(g.dataFine) <= new Date(dateTo) : new Date(g.dataInizio) <= new Date(dateTo));
            return matchesSearch && matchesStagione && matchesStatus && fromOk && toOk;
        });
    }, [giornate, searchTerm, filterStagione, filterStatus, dateFrom, dateTo]);

    const paginatedGiornate = React.useMemo(() => {
        const start = pageG * rowsPerPageG;
        return filteredGiornate.slice(start, start + rowsPerPageG);
    }, [filteredGiornate, pageG, rowsPerPageG]);

    useEffect(() => {
        const fetchLega = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = getJwt();
                if (!token) throw new Error('Token non trovato');
                const res = await axios.get(`${host}/tbl-lega/${legaId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                setLega(res.data);
            } catch (err) {
                setError('Errore nel caricamento dati lega');
            } finally {
                setLoading(false);
            }
        };
        fetchLega();
    }, [legaId]);

    useEffect(() => {
        if (tab === 1 && lega) {
            const fetchGiornate = async () => {
                setLoadingGiornate(true);
                setErrorGiornate(null);
                try {
                    const token = getJwt();
                    if (!token) throw new Error('Token non trovato');
                    const res = await axios.get(`${host}/tbl-giornata`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        params: { legaId: lega.id }
                    });
                    setGiornate(res.data.data || []);
                } catch (err) {
                    setErrorGiornate('Errore nel caricamento delle giornate');
                } finally {
                    setLoadingGiornate(false);
                }
            };
            fetchGiornate();
        }
    }, [tab, lega]);

    useEffect(() => {
        if (tab === 2 && lega) {
            const fetchClassifiche = async () => {
                setLoadingClassifiche(true);
                setErrorClassifiche(null);
                try {
                    const token = getJwt();
                    if (!token) throw new Error('Token non trovato');
                    const res = await axios.get(`${host}/tbl-classifica-snapshot-generale`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        params: { 
                            legaId: lega.id,
                            $limit: 1000,
                            $sort: { stagioneId: -1, posizione: 1 }
                        }
                    });
                    const classificheData = res.data.data || [];
                    setClassifiche(classificheData);

                    // Carica i dati dei team (foto) per tutte le squadre nella classifica
                    const uniqueTeamIds = [...new Set(classificheData.map(c => c.teamId))];
                    if (uniqueTeamIds.length > 0) {
                        const teamsRes = await axios.get(`${host}/tbl-team`, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                            params: {
                                id: { $in: uniqueTeamIds },
                                $limit: uniqueTeamIds.length
                            }
                        });
                        const teamsMap = new Map();
                        (teamsRes.data.data || []).forEach(team => {
                            teamsMap.set(team.id, team);
                        });
                        setTeamsData(teamsMap);
                    }
                } catch (err) {
                    setErrorClassifiche('Errore nel caricamento delle classifiche');
                } finally {
                    setLoadingClassifiche(false);
                }
            };
            fetchClassifiche();
        }
    }, [tab, lega]);

    const getStatusChip = (lega) => {
        if (!lega) return null;
        if (lega.flAnnullato === 1) {
            return <Chip label="Annullata" color="error" size="small" />;
        }
        if (lega.flAttivo === 1) {
            return <Chip label="Attiva" color="success" size="small" />;
        }
        return <Chip label="Inattiva" color="default" size="small" />;
    };

    const getSportName = (sportId) => {
        switch (sportId) {
            case 1: return 'Calcio';
            case 2: return 'Basket';
            case 3: return 'Pallavolo';
            default: return `Sport ${sportId}`;
        }
    };

    return (
        <Box sx={{ p: 0, width: '100%', minHeight: '100vh', bgcolor: '#f5f6fa', overflowX: 'hidden' }}>
            <Box sx={{ p: 3, pb: 0 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                    Torna indietro
                </Button>
                <Button variant="contained" sx={{ ml: 2 }} onClick={() => navigate(`/partite?legaId=${lega?.id}`)}>
                    Vedi partite
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                        <EmojiEventsIcon fontSize="large" />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" fontWeight="bold">{lega?.nome || 'Lega'}</Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {lega ? `ID: #${lega.id} • ${getSportName(lega.sportId)}` : ''}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    {lega && getStatusChip(lega)}
                </Box>
                <Tabs
                    value={tab}
                    onChange={(_, v) => {
                        setTab(v);
                        const tabName = v === 1 ? 'giornate' : v === 2 ? 'classifiche' : 'info';
                        navigate(`?tab=${tabName}`, { replace: true });
                    }}
                    sx={{ mb: 2 }}
                >
                    <Tab label="Dettagli" icon={<EmojiEventsIcon />} iconPosition="start" />
                    <Tab label="Giornate" icon={<EventNoteIcon />} iconPosition="start" />
                    <Tab label="Classifiche" icon={<LeaderboardIcon />} iconPosition="start" />
                </Tabs>
            </Box>
            <Box sx={{ px: 3, pb: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : !lega ? (
                    <Alert severity="info">Lega non trovata.</Alert>
                ) : (
                    <>
                        {tab === 0 ? (
                            <Card sx={{ width: '100%', mb: 2 }}>
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Sport</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <SportsIcon color="action" />
                                                <Typography>{getSportName(lega.sportId)}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Ordinamento</Typography>
                                            <Typography sx={{ mb: 2 }}>{lega.ordinamento ?? '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Note</Typography>
                                            <Typography sx={{ mb: 2 }}>{lega.note || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Data creazione</Typography>
                                            <Typography sx={{ mb: 2 }}>{lega.createdAt ? new Date(lega.createdAt).toLocaleDateString('it-IT') : '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ) : tab === 1 ? (
                    <Card sx={{ width: '100%', mb: 2 }}>
                        <CardContent>
                            {/* Barra filtri */}
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                placeholder="Cerca per nome, numero o ID..."
                                                value={searchTerm}
                                                onChange={(e) => { setSearchTerm(e.target.value); setPageG(0); }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>Stagione</InputLabel>
                                                <Select
                                                    value={filterStagione}
                                                    label="Stagione"
                                                    onChange={(e) => { setFilterStagione(e.target.value); setPageG(0); }}
                                                >
                                                    <MenuItem value="all">Tutte</MenuItem>
                                                    {stagioneOptions.map(id => (
                                                        <MenuItem key={id} value={String(id)}>{id}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>Stato</InputLabel>
                                                <Select
                                                    value={filterStatus}
                                                    label="Stato"
                                                    onChange={(e) => { setFilterStatus(e.target.value); setPageG(0); }}
                                                >
                                                    <MenuItem value="all">Tutti</MenuItem>
                                                    <MenuItem value="active">Attive</MenuItem>
                                                    <MenuItem value="inactive">Inattive</MenuItem>
                                                    <MenuItem value="cancelled">Annullate</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Filtra per periodo - Da">
                                                    <TextField
                                                        type="date"
                                                        size="small"
                                                        value={dateFrom}
                                                        onChange={(e) => { setDateFrom(e.target.value); setPageG(0); }}
                                                    />
                                                </Tooltip>
                                                <Tooltip title="Filtra per periodo - A">
                                                    <TextField
                                                        type="date"
                                                        size="small"
                                                        value={dateTo}
                                                        onChange={(e) => { setDateTo(e.target.value); setPageG(0); }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {loadingGiornate ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : errorGiornate ? (
                                <Alert severity="error">{errorGiornate}</Alert>
                            ) : (
                                <>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Giornate della lega
                                    </Typography>
                                    {giornate.length === 0 ? (
                                        <Alert severity="info">Nessuna giornata trovata per questa lega.</Alert>
                                    ) : (
                                        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: 56, maxWidth: 56, minWidth: 56, textAlign: 'center' }}>#</TableCell>
                                                        <TableCell>Nome</TableCell>
                                                        <TableCell>Numero</TableCell>
                                                        <TableCell>legaStagioneId</TableCell>
                                                        <TableCell>legaId</TableCell>
                                                        <TableCell>stagioneId</TableCell>
                                                        <TableCell>Data inizio</TableCell>
                                                        <TableCell>Data fine</TableCell>
                                                        <TableCell>Stato</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedGiornate.map((g) => (
                                                        <TableRow key={g.id} hover sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                            <TableCell sx={{ width: 56, maxWidth: 56, minWidth: 56, textAlign: 'center', px: 1 }}>
                                                                <Chip
                                                                    label={`#${g.id}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.72rem' } }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.nome || '-'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={g.numero ?? '-'} size="small" />
                                                            </TableCell>
                                                            <TableCell>{g.legaStagioneId ?? '-'}</TableCell>
                                                            <TableCell>{g.legaId ?? '-'}</TableCell>
                                                            <TableCell>{g.stagioneId ?? '-'}</TableCell>
                                                            <TableCell>{g.dataInizio ? new Date(g.dataInizio).toLocaleDateString('it-IT') : '-'}</TableCell>
                                                            <TableCell>{g.dataFine ? new Date(g.dataFine).toLocaleDateString('it-IT') : '-'}</TableCell>
                                                            <TableCell>
                                                                {g.flAnnullato === 1 ? (
                                                                    <Chip label="Annullata" size="small" color="error" />
                                                                ) : g.flAttivo === 1 ? (
                                                                    <Chip label="Attiva" size="small" color="success" />
                                                                ) : (
                                                                    <Chip label="Inattiva" size="small" />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <ButtonGroup size="small" variant="text">
                                                                    <Button onClick={() => { window.location.hash = `/giornate/${g.id}`; }}>Apri Dettaglio</Button>
                                                                    <Button component="a" href={`#/giornate/${g.id}`} target="_blank" endIcon={<OpenInNewIcon />}>Nuovo tab</Button>
                                                                </ButtonGroup>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            {/* Paginazione */}
                                            <TablePagination
                                                component="div"
                                                count={filteredGiornate.length}
                                                page={pageG}
                                                onPageChange={(_, newPage) => setPageG(newPage)}
                                                rowsPerPage={rowsPerPageG}
                                                onRowsPerPageChange={(e) => { setRowsPerPageG(parseInt(e.target.value, 10)); setPageG(0); }}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                labelRowsPerPage="Righe per pagina:"
                                            />
                                        </TableContainer>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                        ) : tab === 2 ? (
                            <Card sx={{ width: '100%', mb: 2 }}>
                                <CardContent>
                                    {/* Filtro stagione per classifiche */}
                                    <Box sx={{ mb: 2 }}>
                                        <FormControl sx={{ minWidth: 200 }}>
                                            <InputLabel>Stagione</InputLabel>
                                            <Select
                                                value={filterStagioneClassifica}
                                                label="Stagione"
                                                onChange={(e) => setFilterStagioneClassifica(e.target.value)}
                                            >
                                                <MenuItem value="all">Tutte le stagioni</MenuItem>
                                                {Array.from(new Set(classifiche.map(c => c.stagioneId))).sort((a, b) => b - a).map(sid => (
                                                    <MenuItem key={sid} value={String(sid)}>Stagione {sid}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {loadingClassifiche ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : errorClassifiche ? (
                                        <Alert severity="error">{errorClassifiche}</Alert>
                                    ) : (
                                        <>
                                            {classifiche.length === 0 ? (
                                                <Alert severity="info">Nessuna classifica disponibile per questa lega.</Alert>
                                            ) : (
                                                <>
                                                    {/* Raggruppa per stagione */}
                                                    {Array.from(new Set(classifiche.map(c => c.stagioneId)))
                                                        .sort((a, b) => b - a)
                                                        .filter(sid => filterStagioneClassifica === 'all' || String(sid) === filterStagioneClassifica)
                                                        .map(stagioneId => {
                                                            const classificaStagione = classifiche
                                                                .filter(c => c.stagioneId === stagioneId)
                                                                .sort((a, b) => (a.posizione || 999) - (b.posizione || 999));
                                                            
                                                            return (
                                                                <Box key={stagioneId} sx={{ mb: 4 }}>
                                                                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <LeaderboardIcon color="primary" />
                                                                        Classifica Stagione {stagioneId}
                                                                        <Chip label={`${classificaStagione.length} squadre`} size="small" color="primary" />
                                                                    </Typography>
                                                                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow sx={{ bgcolor: 'primary.light' }}>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center', width: 60 }}>Pos</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white' }}>Squadra</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>PG</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>V</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>P</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>S</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>GF</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>GS</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>DR</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>Punti</TableCell>
                                                                                    <TableCell sx={{ fontWeight: 700, color: 'white', textAlign: 'center' }}>Ultime 5</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {classificaStagione.map((row, idx) => {
                                                                                    const isTop3 = (row.posizione || idx + 1) <= 3;
                                                                                    const isLast3 = classificaStagione.length >= 3 && (row.posizione || idx + 1) > classificaStagione.length - 3;
                                                                                    
                                                                                    return (
                                                                                        <TableRow 
                                                                                            key={row.id || idx} 
                                                                                            hover
                                                                                            sx={{ 
                                                                                                bgcolor: isTop3 ? 'success.lighter' : isLast3 ? 'error.lighter' : 'inherit',
                                                                                                '&:hover': { bgcolor: isTop3 ? 'success.light' : isLast3 ? 'error.light' : '#fafafa' }
                                                                                            }}
                                                                                        >
                                                                                            <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                                                                    {(row.posizione || idx + 1) === 1 && (
                                                                                                        <EmojiEvents sx={{ color: '#FFD700', fontSize: 28 }} />
                                                                                                    )}
                                                                                                    {(row.posizione || idx + 1) === 2 && (
                                                                                                        <EmojiEvents sx={{ color: '#C0C0C0', fontSize: 26 }} />
                                                                                                    )}
                                                                                                    {(row.posizione || idx + 1) === 3 && (
                                                                                                        <EmojiEvents sx={{ color: '#CD7F32', fontSize: 24 }} />
                                                                                                    )}
                                                                                                    <Chip 
                                                                                                        label={row.posizione || idx + 1} 
                                                                                                        size="small" 
                                                                                                        color={isTop3 ? 'success' : isLast3 ? 'error' : 'default'}
                                                                                                        sx={{ fontWeight: 700 }}
                                                                                                    />
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                                                    <Avatar
                                                                                                        src={teamsData.get(row.teamId)?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(teamsData.get(row.teamId).foto)}` : undefined}
                                                                                                        alt={row.squadra}
                                                                                                        sx={{ width: 32, height: 32 }}
                                                                                                    >
                                                                                                        {row.squadra?.charAt(0).toUpperCase() || '⚽'}
                                                                                                    </Avatar>
                                                                                                    <Typography 
                                                                                                        variant="body2" 
                                                                                                        sx={{ 
                                                                                                            fontWeight: 600,
                                                                                                            cursor: 'pointer',
                                                                                                            color: 'primary.main',
                                                                                                            textDecoration: 'none',
                                                                                                            '&:hover': {
                                                                                                                textDecoration: 'underline'
                                                                                                            }
                                                                                                        }}
                                                                                                        component="a"
                                                                                                        href={`#/squadre/${row.teamId}`}
                                                                                                        onClick={(e) => {
                                                                                                            e.preventDefault();
                                                                                                            navigate(`/squadre/${row.teamId}`);
                                                                                                        }}
                                                                                                    >
                                                                                                        {row.squadra || `Team ${row.teamId}`}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center' }}>{row.partiteGiocate || 0}</TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center', color: 'success.main', fontWeight: 600 }}>{row.vinte || 0}</TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center', color: 'warning.main' }}>{row.pareggiate || 0}</TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>{row.perse || 0}</TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center' }}>{row.goalFatti || 0}</TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center' }}>{row.goalSubiti || 0}</TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center', fontWeight: 600, color: (row.differenzaReti || 0) >= 0 ? 'success.main' : 'error.main' }}>
                                                                                                {(row.differenzaReti || 0) >= 0 ? '+' : ''}{row.differenzaReti || 0}
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center' }}>
                                                                                                <Chip 
                                                                                                    label={row.punti || 0} 
                                                                                                    size="small" 
                                                                                                    color="primary"
                                                                                                    sx={{ fontWeight: 700, fontSize: '0.9rem' }}
                                                                                                />
                                                                                            </TableCell>
                                                                                            <TableCell sx={{ textAlign: 'center' }}>
                                                                                                {row.ultime5 ? (
                                                                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                                                        {row.ultime5.split('').map((char, i) => (
                                                                                                            <Chip 
                                                                                                                key={i}
                                                                                                                label={char}
                                                                                                                size="small"
                                                                                                                sx={{ 
                                                                                                                    width: 24, 
                                                                                                                    height: 24,
                                                                                                                    fontSize: '0.7rem',
                                                                                                                    bgcolor: char === 'V' ? 'success.main' : char === 'P' ? 'warning.main' : char === 'S' ? 'error.main' : 'grey.400',
                                                                                                                    color: 'white',
                                                                                                                    fontWeight: 700
                                                                                                                }}
                                                                                                            />
                                                                                                        ))}
                                                                                                    </Box>
                                                                                                ) : '-'}
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    );
                                                                                })}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </TableContainer>
                                                                </Box>
                                                            );
                                                        })}
                                                </>
                                            )}
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        ) : null}
                    </>
                )}
            </Box>
        </Box>
    );
}

export default LegaDetailPage;
